import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CreateCustomerReportInput,
  CustomerReport,
  CustomerReportDeliveryResult,
  CustomerReportMemberOption,
  CustomerReportPreview,
  CustomerReportSendPlanItem,
  SetCustomerReportEnrollmentsInput,
  UpdateCustomerReportInput,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';

const customerReportKeys = {
  all: ['customer-reports'] as const,
  list: () => ['customer-reports', 'list'] as const,
  memberOptions: () => ['customer-reports', 'member-options'] as const,
  detail: (id: string) => ['customer-reports', 'detail', id] as const,
  preview: (id: string, customerId: string) =>
    ['customer-reports', 'preview', id, customerId] as const,
  sendPlan: (id: string) => ['customer-reports', 'send-plan', id] as const,
};

export function useCustomerReports(): UseQueryResult<CustomerReport[]> {
  return useQuery({
    queryKey: customerReportKeys.list(),
    queryFn: () => api.get<CustomerReport[]>('/customer-reports'),
  });
}

export function useCustomerReportMemberOptions(): UseQueryResult<CustomerReportMemberOption[]> {
  return useQuery({
    queryKey: customerReportKeys.memberOptions(),
    queryFn: () => api.get<CustomerReportMemberOption[]>('/customer-reports/member-options'),
  });
}

export function useCreateCustomerReport(): UseMutationResult<
  CustomerReport,
  unknown,
  CreateCustomerReportInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCustomerReportInput) =>
      api.post<CustomerReport>('/customer-reports', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerReportKeys.all });
    },
  });
}

export interface UpdateCustomerReportArgs {
  id: string;
  input: UpdateCustomerReportInput;
}

export function useUpdateCustomerReport(): UseMutationResult<
  CustomerReport,
  unknown,
  UpdateCustomerReportArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateCustomerReportArgs) =>
      api.patch<CustomerReport>(`/customer-reports/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerReportKeys.all });
    },
  });
}

export function useDeleteCustomerReport(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/customer-reports/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerReportKeys.all });
    },
  });
}

export interface SetEnrollmentsArgs {
  id: string;
  input: SetCustomerReportEnrollmentsInput;
}

export function useSetCustomerReportEnrollments(): UseMutationResult<
  CustomerReport,
  unknown,
  SetEnrollmentsArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: SetEnrollmentsArgs) =>
      api.put<CustomerReport>(`/customer-reports/${id}/enrollments`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerReportKeys.all });
    },
  });
}

/** Renders a report preview for a single enrolled customer. */
export function useCustomerReportPreview(
  id: string,
  customerId: string | null,
  enabled: boolean,
): UseQueryResult<CustomerReportPreview> {
  return useQuery({
    queryKey: customerReportKeys.preview(id, customerId ?? ''),
    queryFn: () =>
      api.get<CustomerReportPreview>(`/customer-reports/${id}/preview/${customerId ?? ''}`),
    enabled: enabled && Boolean(customerId),
  });
}

/** Whether each enrolled customer would be sent or skipped, evaluated without sending. */
export function useCustomerReportSendPlan(
  id: string,
  enabled: boolean,
): UseQueryResult<CustomerReportSendPlanItem[]> {
  return useQuery({
    queryKey: customerReportKeys.sendPlan(id),
    queryFn: () =>
      api.get<CustomerReportSendPlanItem[]>(`/customer-reports/${id}/send-plan`),
    enabled,
  });
}

/** Sends a report to all enrolled customers now. */
export function useSendCustomerReport(): UseMutationResult<
  CustomerReportDeliveryResult,
  unknown,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<CustomerReportDeliveryResult>(`/customer-reports/${id}/send`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerReportKeys.all });
    },
  });
}
